<script setup lang="ts">
import { computed, useId } from 'vue';
import type { ProductInterface } from '#server/types/Products';

type Term = 1 | 3 | 6 | 12;
const props = defineProps<{
  modelValue: Term | null;
  subscription: NonNullable<ProductInterface['subscription']>;
  quantity: number;
  checkoutAvailable: boolean;
  monthlyBasePrice: number;
  shippingMode?: 'prepaid_period' | 'per_order' | 'free' | null;
}>();
const emit = defineEmits<{ 'update:modelValue': [Term | null] }>();
const auth = useAuthStore();
const { format } = usePrice();
const id = useId();
const offers = computed(() => Object.entries(props.subscription.terms).map(([months, discount]) => ({
  months: Number(months) as Term,
  discount: Number(discount),
})).sort((a, b) => a.months - b.months));
const maxDiscount = computed(() => Math.max(0, ...offers.value.map(offer => offer.discount)));
const selectSubscription = () => emit('update:modelValue', props.modelValue ?? offers.value[0]?.months ?? null);
const monthlyPrice = (discount: number) => {
  if (!Number.isFinite(props.monthlyBasePrice) || !Number.isFinite(props.quantity) || props.quantity <= 0) return null;
  const unitMinor = Math.round((props.monthlyBasePrice / props.quantity + Number.EPSILON) * 100);
  const discountedUnitMinor = Math.round(unitMinor * (10000 - Math.round(discount * 100)) / 10000);
  return Math.round(discountedUnitMinor * props.quantity) / 100;
};
</script>

<template>
  <section class="subscription-terms" :aria-label="$t('subscription_purchase')">
    <div v-if="!auth.user" class="subscription-signin">
      <Icon name="lucide:user-round" size="19" aria-hidden="true" />
      <div>
        <strong>{{ $t('subscription_account_title') }}</strong>
        <p>{{ $t('subscription_account_help') }}</p>
        <NuxtLinkLocale to="/login" class="subscription-signin-link">
          {{ $t('subscription_sign_in') }} <span aria-hidden="true">→</span>
        </NuxtLinkLocale>
      </div>
    </div>

    <fieldset class="purchase-options">
      <legend>{{ $t('subscription_purchase') }}</legend>
      <label class="purchase-option" :class="{ 'is-selected': !modelValue }">
        <input type="radio" :name="`${id}-purchase`" :checked="!modelValue" @change="emit('update:modelValue', null)" />
        <span class="purchase-option-content">
          <strong>{{ $t('subscription_one_time') }}</strong>
          <span class="purchase-option-note">{{ $t('subscription_one_time_help') }}</span>
        </span>
      </label>
      <label class="purchase-option" :class="{ 'is-selected': modelValue }">
        <input type="radio" :name="`${id}-purchase`" :checked="Boolean(modelValue)" @change="selectSubscription" />
        <span class="purchase-option-content">
          <span class="purchase-option-title">
            <strong>{{ $t('subscription_subscribe') }}</strong>
            <span v-if="maxDiscount" class="subscription-saving">{{ $t('subscription_save_up_to', { percent: maxDiscount }) }}</span>
          </span>
          <span class="purchase-option-note">{{ $t('subscription_monthly_delivery') }}</span>
        </span>
      </label>
    </fieldset>

    <div v-if="modelValue" class="subscription-selection">
      <fieldset class="subscription-plans">
        <legend>{{ $t('subscription_choose_term') }}</legend>
        <div class="subscription-plan-grid">
          <label v-for="offer in offers" :key="offer.months" class="subscription-plan" :class="{ 'is-selected': modelValue === offer.months }">
            <input type="radio" :name="`${id}-term`" :checked="modelValue === offer.months" @change="emit('update:modelValue', offer.months)" />
            <strong>{{ $t('subscription_month_count', { months: offer.months }) }}</strong>
            <span v-if="monthlyPrice(offer.discount) !== null" class="subscription-plan-price">
              <strong>{{ format(monthlyPrice(offer.discount)!) }}</strong>
              <span>{{ $t('subscription_per_month') }}</span>
            </span>
            <span v-if="offer.discount > 0" class="subscription-saving">−{{ offer.discount }}%</span>
            <span v-else class="subscription-plan-note">{{ $t('subscription_no_discount') }}</span>
          </label>
        </div>
      </fieldset>
      <div class="subscription-explanation">
        <div class="subscription-explanation-row">
          <Icon name="lucide:package-check" size="17" aria-hidden="true" />
          <p><strong>{{ $t('subscription_quantity', { quantity }) }}</strong><span>{{ $t('subscription_delivery_count', { count: modelValue }) }}</span></p>
        </div>
        <div class="subscription-explanation-row">
          <Icon name="lucide:wallet" size="17" aria-hidden="true" />
          <p>{{ $t('subscription_prepaid_goods', { months: modelValue }) }}</p>
        </div>
        <div v-if="shippingMode" class="subscription-explanation-row">
          <Icon name="lucide:truck" size="17" aria-hidden="true" />
          <p>{{ $t(shippingMode === 'free' ? 'subscription_shipping_free' : shippingMode === 'per_order' ? 'subscription_shipping_per_order' : 'subscription_shipping_prepaid_checkout') }}</p>
        </div>
      </div>
      <p class="subscription-fine-print">{{ $t('subscription_final_price_cart') }}</p>
      <p class="subscription-fine-print">{{ $t('subscription_renewal_help') }}</p>
      <el-alert v-if="!checkoutAvailable" class="mt-3" :title="$t('subscription_checkout_unavailable')" type="info" :closable="false" />
    </div>
  </section>
</template>

<style scoped>
.subscription-terms { margin: 20px 0; color: var(--el-text-color-primary); }
.subscription-signin { display: flex; align-items: flex-start; gap: 10px; padding: 14px; margin-bottom: 18px; border-radius: 12px; background: var(--el-color-primary-light-9); }
.subscription-signin > :first-child { flex-shrink: 0; margin-top: 2px; color: var(--el-color-primary); }
.subscription-signin strong { display: block; font-size: 14px; line-height: 1.4; }
.subscription-signin p { margin: 4px 0 8px; font-size: 12px; line-height: 1.55; color: var(--el-text-color-regular); }
.subscription-signin-link { font-size: 13px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
.purchase-options, .subscription-plans { min-width: 0; padding: 0; margin: 0; border: 0; }
.purchase-options legend, .subscription-plans legend { float: none; width: auto; margin-bottom: 10px; font-size: 13px; font-weight: 600; color: var(--el-text-color-regular); }
.purchase-option { display: flex; align-items: flex-start; gap: 10px; padding: 14px 12px; margin-bottom: 9px; border: 1px solid var(--el-border-color); border-radius: 12px; background: var(--el-bg-color); cursor: pointer; transition: border-color .15s, background-color .15s; }
.purchase-option:hover, .subscription-plan:hover { border-color: var(--el-color-primary); }
.purchase-option.is-selected, .subscription-plan.is-selected { border-color: var(--el-color-primary); background: var(--el-color-primary-light-9); box-shadow: inset 0 0 0 1px var(--el-color-primary); }
.purchase-option input { flex-shrink: 0; margin: 3px 0 0; width: 16px; height: 16px; accent-color: var(--el-color-primary); }
.purchase-option:focus-within, .subscription-plan:focus-within { outline: 2px solid var(--el-color-primary); outline-offset: 3px; }
.purchase-option-content { min-width: 0; flex: 1; }
.purchase-option-content strong { font-size: 14px; line-height: 1.4; }
.purchase-option-title { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; }
.purchase-option-note { display: block; margin-top: 4px; color: var(--el-text-color-regular); font-size: 12px; line-height: 1.5; }
.subscription-saving { display: inline-flex; width: fit-content; padding: 3px 7px; border-radius: 6px; background: var(--el-color-primary-light-8); color: var(--el-text-color-primary); font-size: 11px; font-weight: 600; line-height: 1.4; white-space: nowrap; }
.subscription-selection { padding-top: 8px; }
.subscription-plan-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.subscription-plan { position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 14px 10px; border: 1px solid var(--el-border-color); border-radius: 12px; background: var(--el-bg-color); cursor: pointer; }
.subscription-plan input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.subscription-plan strong { font-size: 16px; line-height: 1.4; }
.subscription-plan-price { display: flex; flex-direction: column; gap: 1px; }
.subscription-plan-price strong { font-size: 20px; letter-spacing: -.5px; }
.subscription-plan-price > span { font-size: 11px; color: var(--el-text-color-regular); }
.subscription-plan-note { font-size: 11px; color: var(--el-text-color-regular); }
.subscription-explanation { display: grid; gap: 12px; margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--el-border-color-lighter); }
.subscription-explanation-row { display: flex; align-items: flex-start; gap: 9px; }
.subscription-explanation-row > :first-child { flex-shrink: 0; color: var(--el-color-primary); margin-top: 2px; }
.subscription-explanation-row p { margin: 0; color: var(--el-text-color-regular); font-size: 12px; line-height: 1.55; }
.subscription-explanation-row p strong, .subscription-explanation-row p span { display: block; }
.subscription-explanation-row strong { color: var(--el-text-color-primary); }
.subscription-fine-print { margin: 12px 0 0; font-size: 11px; line-height: 1.6; color: var(--el-text-color-regular); }
@media (prefers-reduced-motion: reduce) { .purchase-option { transition: none; } }
</style>
